import { useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export interface BookingFormData {
  clientId?: string | null;
  accountName: string;
  contactName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  timezone?: string;
  clientType?: string;
  region?: string;
  talentId?: string;
  eventOfferingId?: string;
  eventDetails?: string;
  rate?: string;
  discount?: string;
  dateOfEvent?: string;
  eventTime?: string;
  calendarNotes?: string;
  notes?: string;
  rep: string;
  isTicketedEvent?: boolean;
  isNoFeeShow?: boolean;
  skipCalendar?: boolean;
  skipEmailDraft?: boolean;
  forceCreateClient?: boolean;
  forceTalentOverlap?: boolean;
  [key: string]: unknown;
}

export interface DuplicateCandidate {
  id: string;
  accountName: string;
  contactName?: string;
  emailAddress?: string;
  phoneNumber?: string;
  address?: string;
}

export interface TalentConflict {
  id: string;
  invoiceNumber: string;
  accountName: string;
  dateOfEvent: string | Date;
  status: string;
}

export interface DoubleBookingConflict {
  message: string;
  talentConflicts: TalentConflict[];
  pendingFormData: BookingFormData;
}

export interface CalendarPushFailure {
  talentName: string;
  talentId: string;
  /** The just-created clientId + invoiceId so the frontend can roll back */
  clientId: string;
  invoiceId: string;
  /**
   * Calendar event ID that was created via workspace-fallback BEFORE the
   * talent-calendar failure. Present only for NO_CALENDAR_CONNECTED when the
   * workspace has its own calendar connected. Pass to /api/bookings/rollback
   * so it can delete the event and leave nothing behind on Cancel.
   */
  calendarEventId?: string | null;
  /**
   * Whether the client record was newly created in this booking (true) or
   * was an existing re-book client (false). Rollback must NOT delete the
   * client when false — only the newly created invoice should be rolled back.
   */
  isNewClient: boolean;
}

export interface BookingResult {
  success: boolean;
  step: "client" | "client_duplicates" | "invoice" | "calendar" | "email" | "complete" | "talent_double_booking" | "calendar_push_failed" | "unknown";
  message: string;
  data?: {
    clientId: string;
    client: {
      id: string;
      accountName: string;
      isNew: boolean;
    };
    invoice: {
      id: string;
      invoiceNumber: string;
      status: string;
      publicToken: string;
    };
    calendar: {
      eventId: string;
      htmlLink: string;
    } | null;
    emailDraft: {
      subject: string;
      body: string;
      recipientEmail: string;
      recipientName: string;
      templateId?: string;
    } | null;
  };
  calendarFailure?: {
    talentName: string;
    talentId: string;
  };
  duplicateCandidates?: DuplicateCandidate[];
  talentConflicts?: TalentConflict[];
  canOverride?: boolean;
  logs?: string[];
  errors?: any;
}

interface UseBookingOrchestrationOptions {
  onSuccess?: (result: BookingResult) => void;
  onDuplicatesFound?: (candidates: DuplicateCandidate[]) => void;
  onDoubleBookingConflict?: (conflict: DoubleBookingConflict) => void;
  onCalendarPushFailed?: (failure: CalendarPushFailure) => void;
  onError?: (error: Error, step: string) => void;
}

export function useBookingOrchestration(options?: UseBookingOrchestrationOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const lastFormData = useRef<BookingFormData | null>(null);

  const mutation = useMutation({
    mutationFn: async (formData: BookingFormData): Promise<BookingResult> => {
      console.log("[BookingOrchestration] Starting orchestration with data:", formData);
      lastFormData.current = formData;

      let response: Response;
      try {
        response = await apiRequest("POST", "/api/bookings/orchestrate", {
          clientId: formData.clientId,
          accountName: formData.accountName,
          contactName: formData.contactName,
          emailAddress: formData.emailAddress,
          phoneNumber: formData.phoneNumber,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          timezone: formData.timezone,
          clientType: formData.clientType,
          region: formData.region,
          talentId: formData.talentId,
          eventOfferingId: formData.eventOfferingId,
          eventDetails: formData.eventDetails,
          rate: formData.rate,
          discount: formData.discount || "0",
          dateOfEvent: formData.dateOfEvent,
          eventTime: formData.eventTime,
          calendarNotes: formData.calendarNotes,
          notes: formData.notes,
          rep: formData.rep,
          isTicketedEvent: formData.isTicketedEvent || false,
          isNoFeeShow: formData.isNoFeeShow || false,
          skipCalendar: formData.skipCalendar || false,
          skipEmailDraft: formData.skipEmailDraft || false,
          forceCreateClient: formData.forceCreateClient || false,
          forceTalentOverlap: formData.forceTalentOverlap || false,
          // Forward any extra fields callers attach (e.g. runId, bookingTalents)
          ...Object.fromEntries(
            Object.entries(formData).filter(([k]) =>
              !["clientId","accountName","contactName","emailAddress","phoneNumber","address",
                "city","state","zipCode","timezone","clientType","region","talentId",
                "eventOfferingId","eventDetails","rate","discount","dateOfEvent","eventTime",
                "calendarNotes","notes","rep","isTicketedEvent","isNoFeeShow","skipCalendar",
                "skipEmailDraft","forceCreateClient","forceTalentOverlap"].includes(k)
            )
          ),
        });
      } catch (err: any) {
        // Intercept 409 double-booking: parse the body from the thrown error
        // message and return it as a resolved BookingResult so the confirmation
        // flow (onDoubleBookingConflict) can handle it rather than onError.
        const raw: string = err?.message || "";
        if (raw.startsWith("409:")) {
          try {
            const body = JSON.parse(raw.slice(4).trim());
            if (body?.step === "talent_double_booking") {
              console.log("[BookingOrchestration] Caught double-booking 409, surfacing as conflict");
              return body as BookingResult;
            }
          } catch {}
        }
        throw err;
      }

      const result: BookingResult = await response.json();

      console.log("[BookingOrchestration] Result:", result);

      if (result.logs) {
        result.logs.forEach(log => console.log(log));
      }

      return result;
    },
    onSuccess: (result) => {
      // Double-booking guard fired — route to the confirmation callback.
      if (result.step === "talent_double_booking") {
        if (options?.onDoubleBookingConflict) {
          options.onDoubleBookingConflict({
            message: result.message,
            talentConflicts: result.talentConflicts || [],
            pendingFormData: lastFormData.current!,
          });
        } else {
          toast({
            title: "Double Booking Detected",
            description: result.message,
            variant: "destructive",
          });
        }
        return;
      }

      // Calendar token revoked/invalid — the booking was created (client +
      // invoice exist) but no calendar event was pushed. Surface this as a
      // blocking choice: "Book anyways" or "Cancel" (rolls back).
      if (result.step === "calendar_push_failed") {
        if (options?.onCalendarPushFailed) {
          options.onCalendarPushFailed({
            talentName: result.calendarFailure?.talentName ?? "the talent",
            talentId: result.calendarFailure?.talentId ?? "",
            clientId: result.data?.clientId ?? "",
            invoiceId: result.data?.invoice?.id ?? "",
            calendarEventId: result.calendarFailure?.calendarEventId ?? null,
            isNewClient: result.calendarFailure?.isNewClient ?? result.data?.client?.isNew ?? true,
          });
        } else {
          // Fallback when caller didn't register the callback: treat as
          // success so the booking isn't lost (same as "Book anyways").
          queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
          queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
          toast({
            title: "Booked (no calendar)",
            description: `Calendar sync failed for ${result.calendarFailure?.talentName ?? "the talent"} — please reconnect their calendar in the Admin Panel.`,
            variant: "destructive",
          });
          options?.onSuccess?.(result);
        }
        return;
      }

      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });
        queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
        options?.onSuccess?.(result);
      } else if (result.step === "client_duplicates" && result.duplicateCandidates) {
        options?.onDuplicatesFound?.(result.duplicateCandidates);
      } else {
        toast({
          title: "Booking Error",
          description: result.message || "Failed to complete booking",
          variant: "destructive",
        });
        options?.onError?.(new Error(result.message), result.step);
      }
    },
    onError: (error: Error) => {
      console.error("[BookingOrchestration] Error:", error);
      // Surface only the human-readable .message from any JSON error body
      // instead of dumping the raw status + body string.
      const raw = error.message || "";
      const colonIdx = raw.indexOf(":");
      if (colonIdx !== -1) {
        try {
          const body = JSON.parse(raw.slice(colonIdx + 1).trim());
          if (body?.message) {
            toast({
              title: "Booking Failed",
              description: body.message,
              variant: "destructive",
            });
            options?.onError?.(new Error(body.message), body.step || "unknown");
            return;
          }
        } catch {}
      }
      toast({
        title: "Booking Failed",
        description: error.message || "Could not complete the booking.",
        variant: "destructive",
      });
      options?.onError?.(error, "unknown");
    },
  });

  const orchestrate = (formData: BookingFormData) => {
    return mutation.mutateAsync(formData);
  };

  const orchestrateWithClientId = (formData: BookingFormData, clientId: string) => {
    return mutation.mutateAsync({ ...formData, clientId });
  };

  return {
    orchestrate,
    orchestrateWithClientId,
    isPending: mutation.isPending,
    isError: mutation.isError,
    error: mutation.error,
    reset: mutation.reset,
  };
}
